import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Books", to: "/books" },
  { label: "About Damali", to: "/author" },
  { label: "Worksheets", to: "/#worksheets" },
  { label: "Events", to: "/#events" },
  { label: "Writers", to: "/#writers" },
  { label: "Schools", to: "/#schools" },
  { label: "Games", to: "/games" },
  { label: "Blog", to: "/blog" },
];

export default function Navbar() {
  const { count, setOpen } = useCart();
  const [mobile, setMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (to) => {
    setMobile(false);
    if (to.includes("#")) {
      const [path, hash] = to.split("#");
      const targetPath = path || "/";
      if (location.pathname !== targetPath) {
        navigate(targetPath, { state: { scrollTo: hash } });
      } else {
        const el = document.getElementById(hash);
        if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <header data-testid="navigation-bar" className="fixed top-0 inset-x-0 z-50 border-b border-sand/70 bg-cream/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <img
            src="/images/lwb-logo.png"
            alt="Learn With Bailey logo"
            className="h-11 w-auto group-hover:scale-105 transition-transform duration-300"
          />
          <span className="font-display font-bold text-lg tracking-tight text-walnut">
            Learn With <span className="text-terracotta">Bailey</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(l.to)}
              className="text-sm font-medium text-walnutsoft hover:text-walnut transition-colors relative after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-0 after:bg-terracotta after:transition-all hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="cart-drawer-toggle"
            onClick={() => setOpen(true)}
            className="relative flex items-center gap-2 rounded-full bg-walnut text-cream pl-4 pr-5 py-2.5 text-sm font-semibold hover:bg-terracotta transition-colors duration-300"
          >
            <ShoppingBag size={16} />
            <span className="hidden sm:inline">Basket</span>
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-terracotta text-cream text-[11px] font-bold flex items-center justify-center border-2 border-cream">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobile((v) => !v)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-sand text-walnut"
            aria-label="Menu"
          >
            {mobile ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobile && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-sand/70 bg-cream"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => go(l.to)}
                  className="text-left font-display font-semibold text-xl py-3 text-walnut border-b border-sand/60 last:border-0"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
